// The Management per-artist page (`/roster/artists/:artistId`) — the landing when an artist is opened under the
// MANAGEMENT lens (moneyScope 'full'). The money-aware sibling of the Label RosterArtistDashboard: it reads the SAME
// band-scoped composed read (`useRosterArtistDashboard`) plus the extra `heartbeat` block the server populates only for
// a Management caller — next shows, TOURING REVENUE (money — present only for money:lifetime) and top cities. Money is
// a SEPARATE gated block (invariant 7): the Shows card renders ONLY when the server ships `heartbeat.touring`.
// Compact layout (ART-332): release card · the three short cards · Music|Marketing · Shows|Trending · Top cities.
// Four-state contract; born-blank fields render '—'.
import type { components } from "@cntrl/api-client";
import { useRosterArtistDashboard } from "../../app/hooks/useRosterArtistDashboard.js";
import { useArtistReleaseCard } from "../../app/hooks/useReleaseCards.js";
import { useRosterHeaderPills } from "../../app/rosterHeaderPills.js";
import type { ReleaseSectionKey } from "../releases/sections.js";
import { ArtistReleaseCard } from "./ReleaseCards.js";
import { RosterArtistScreenShell, type RosterScreenState } from "./RosterScreenShell.js";
import { formatMoneyCents } from "../../app/hooks/inboxFormat.js";
import {
  ArtistAttentionRow, MusicCard, MarketingCard, TrendingCard, SummaryCard, SummaryRow,
} from "./rosterArtistCards.js";
import { TopCitiesCard } from "../dashboard/DashboardAnalytics.js";
import styles from "./RosterArtistHeartbeat.module.scss";

type ShowSummary = components["schemas"]["ShowSummary"];
type BookingKpis = components["schemas"]["BookingKpis"];

function money(cents: number | null | undefined): string {
  return cents == null ? "—" : formatMoneyCents(cents);
}

/**
 * The SCHEDULE card — legacy's fourth summary card, in the same `SummaryCard` shell as Music, Marketing and
 * Content. It replaced a bespoke four-tile money row (Guarantees / Merch / Tickets / P&L), which was the
 * only card on the page not shaped like its siblings.
 *
 * MONEY-BEARING, and gated whole: `heartbeat.touring` is null unless the caller holds `money:lifetime`, so
 * a seat without it gets no card at all rather than a card with its lead dashed — the whole-or-nothing
 * money-block discipline (invariant 7), and the reason this takes `touring` as a required prop.
 */
function ScheduleCard({
  touring,
  confirmedUpcoming,
  nextShow,
  onOpen,
}: {
  touring: BookingKpis;
  confirmedUpcoming: number;
  nextShow: ShowSummary | undefined;
  onOpen?: () => void;
}) {
  return (
    <SummaryCard
      title="Shows"
      openLabel="Open"
      onOpen={onOpen}
      // `BookingKpis`' flat figures ARE the period ones (default YTD), so guarantees-to-date is the same
      // quantity the booking cockpit calls `touringYtdCents` — one number, two surfaces, one meaning.
      lead={touring.guaranteesCents != null ? money(touring.guaranteesCents) : null}
      leadLabel="Touring revenue · YTD"
    >
      {/* The SERVER's count over every confirmed date ahead. `nextShows` is capped at three for display, so
          deriving it there would print "3" for every busy artist (ART-100). */}
      <SummaryRow label="Confirmed · upcoming" value={confirmedUpcoming > 0 ? String(confirmedUpcoming) : null} />
      {/* Avg. ticket fill has ONE authority — `IBookingArtistDashboardService`, which averages sold/capacity
          over THIS YEAR's confirmed shows — and it is not on this payload. Dashed rather than re-derived
          here from a different set of shows, which is exactly how that figure came to describe the wrong
          window once already. Legacy dashes it too. */}
      <SummaryRow label="Avg. ticket fill" value={null} sub="not set" />
      <SummaryRow
        label="Next show"
        value={nextShow ? [nextShow.venueName, nextShow.city].filter(Boolean).join(" · ") || null : null}
      />
    </SummaryCard>
  );
}

export function RosterArtistHeartbeat({
  onOpenTab,
  onOpenTask,
  onOpenRelease,
}: {
  onOpenTab: (tabKey: string) => void;
  onOpenTask?: (taskId: string) => void;
  /** The release card (ART-332): open a release, at one of its sections. */
  onOpenRelease?: (releaseId: string, section?: ReleaseSectionKey) => void;
}) {
  const q = useRosterArtistDashboard(); // reads the band from the mounted RosterBandProvider (useBandQuery)
  const releaseCard = useArtistReleaseCard();
  const data = q.data;
  // No header action (ART-324): "+ Add task" moved into the To-do card's red + in the top row. The header carries
  // the artist's social pills instead (ART-332), which is what the Content card used to say.
  useRosterHeaderPills(data?.content?.platforms);

  // The shared per-artist frame. Its default skeleton (a header block over a four-card grid) is this
  // page's shape too — the two dashboards had byte-identical loading blocks in two stylesheets.
  const state: RosterScreenState = q.isLoading
    ? { status: "loading" }
    : q.isError || !data
      ? { status: "error", error: q.error, onRetry: () => void q.refetch() }
      : { status: "ready" };

  // Bound after the state resolves so the guard narrows it for everything below — `ready` implies a
  // loaded read, but only to a reader, not to the compiler.
  if (state.status !== "ready" || !data) return <RosterArtistScreenShell state={state} />;

  // The `heartbeat` block is populated ONLY for a Management caller; a money-free lens (Label) gets null/absent.
  // Read everything defensively (Swashbuckle non-required) — a null heartbeat degrades to the money-free cards.
  const heartbeat = data.heartbeat ?? null;
  const nextShows = heartbeat?.nextShows ?? [];
  const touring = heartbeat?.touring ?? null;

  return (
    <RosterArtistScreenShell state={state}>
      {/* The compact layout (ART-332, product owner): the release card (the Label's own component), the three
          short cards, then Music · Marketing and Shows · Trending two-by-two, Top cities at the bottom. The week
          strip and the Next shows card went — Shows' next date and Needs attention's holds cover them — and the
          Content card went to the header's social pills. */}
      {releaseCard.data?.card && onOpenRelease && (
        <ArtistReleaseCard card={releaseCard.data.card} onOpenRelease={onOpenRelease} />
      )}

      <ArtistAttentionRow
        opportunities={data.opportunities}
        todos={data.todos}
        onOpenTask={onOpenTask}
        onChanged={() => void q.refetch()}
        canOpenShows
      />

      <div className={styles.pairEven}>
        <MusicCard music={data.music} onOpen={() => onOpenTab("music")} />
        <MarketingCard marketing={data.marketing} onOpen={() => onOpenTab("marketing")} />
        {/* MONEY gate (invariant 7): the Shows card mounts ONLY when the server ships the touring block
            (money:lifetime), whole-or-nothing — never a card with its lead dashed for a seat without it. */}
        {touring && (
          <ScheduleCard
            touring={touring}
            confirmedUpcoming={heartbeat?.confirmedUpcoming ?? 0}
            nextShow={nextShows[0]}
            onOpen={() => onOpenTab("schedule")}
          />
        )}
        {/* The artist's OWN spike list — the same card the Label per-artist page renders. */}
        <TrendingCard trending={data.trending} />
      </div>

      {/* Top Cities — the ARTIST's own card, not a reduced ranked list. Legacy made exactly this change
          (`top-cities-mirror`, 2026-07-06): "the roster Management view now renders the exact artist-side
          Top Cities table (same component + same band-scoped data)". The BLOCK is handed in rather than
          re-fetched: `stats.TopCities` is already on the composed read this page made. */}
      <TopCitiesCard block={heartbeat?.topCities} />
    </RosterArtistScreenShell>
  );
}
